/* ============================================================
   IMAGE ZOOM MODAL
   Full-screen lens-style zoom for product images.
   Shared by: product.js & configurator.js
   ============================================================ */

class ImageZoomModal {
  constructor() {
    this.modal = document.getElementById("zoom-modal");
    if (!this.modal) return;

    this.images = [];
    this.currentIndex = 0;
    this.zoomLevel = 3;
    this.rect = null;

    this.cacheDOM();
    this.loadImages();
    this.bindEvents();
  }

  cacheDOM() {
    this.imgEl = document.getElementById("zoom-modal-img");
    this.lensEl = document.getElementById("zoom-modal-lens");
    this.resultEl = document.getElementById("zoom-modal-result");
    this.closeBtn = document.getElementById("zoom-modal-close");
    this.prevBtn = document.getElementById("zoom-modal-prev");
    this.nextBtn = document.getElementById("zoom-modal-next");
    this.counterEl = document.getElementById("zoom-modal-counter");
    this.mainEl = document.getElementById("zoom-modal-main");
  }

  loadImages() {
    const dataEl = document.getElementById("ZoomModalData");
    if (!dataEl) return;
    try {
      this.images = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn("ImageZoomModal: failed to parse image data", e);
    }
  }

  bindEvents() {
    this.closeBtn.addEventListener("click", () => this.close());

    this.prevBtn.addEventListener("click", () => this.navigate(-1));
    this.nextBtn.addEventListener("click", () => this.navigate(1));

    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });

    document.addEventListener("keydown", (e) => {
      if (this.modal.hidden) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        this.close();
      }
      if (e.key === "ArrowLeft") this.navigate(-1);
      if (e.key === "ArrowRight") this.navigate(1);
    });

    /* Desktop: lens zoom */
    this.mainEl.addEventListener("mouseenter", () => this.showLens());
    this.mainEl.addEventListener("mouseleave", () => this.hideLens());
    this.mainEl.addEventListener("mousemove", (e) => this.handleMouseMove(e));

    /* Recalculate on resize */
    window.addEventListener("resize", () => {
      if (!this.modal.hidden) this.updateRect();
    });

    /* Mobile: tap to toggle zoom */
    this.isMobile = matchMedia("(hover: none)").matches;
    if (this.isMobile) {
      this.initMobileZoom();
    }
  }

  /* ---- Open / Close ---- */

  open(startIndex = 0) {
    if (!this.images.length) return;
    this.currentIndex = Math.min(startIndex, this.images.length - 1);
    this.loadCurrentImage();
    this.modal.hidden = false;
    document.body.classList.add("zoom-modal-open");
    this.closeBtn.focus();
  }

  close() {
    this.modal.hidden = true;
    document.body.classList.remove("zoom-modal-open");
    this.hideLens();
    if (this.isMobile) this.resetMobileZoom();
  }

  /* ---- Navigation ---- */

  navigate(direction) {
    if (this.images.length <= 1) return;
    this.currentIndex =
      (this.currentIndex + direction + this.images.length) % this.images.length;
    this.loadCurrentImage();
    if (this.isMobile) this.resetMobileZoom();
  }

  loadCurrentImage() {
    const img = this.images[this.currentIndex];
    if (!img) return;

    const hiRes = new Image();
    hiRes.crossOrigin = "anonymous";
    hiRes.onload = () => {
      this.imgEl.src = hiRes.src;
      this.imgEl.alt = img.alt;
      this.imgEl.width = img.width;
      this.imgEl.height = img.height;
      this.resultEl.style.backgroundImage = `url(${hiRes.src})`;

      requestAnimationFrame(() => {
        this.updateRect();
        this.updateResultSize();
      });
    };
    hiRes.src = img.src;

    this.counterEl.textContent = `${this.currentIndex + 1} / ${this.images.length}`;

    const showNav = this.images.length > 1;
    this.prevBtn.style.display = showNav ? "" : "none";
    this.nextBtn.style.display = showNav ? "" : "none";
  }

  /* ---- Lens Zoom (Desktop) ---- */

  updateRect() {
    this.rect = this.imgEl.getBoundingClientRect();
  }

  updateResultSize() {
    if (!this.rect) return;
    this.resultEl.style.backgroundSize = `${this.rect.width * this.zoomLevel}px ${this.rect.height * this.zoomLevel}px`;
  }

  showLens() {
    if (this.isMobile) return;
    this.updateRect();
    this.updateResultSize();
    this.lensEl.style.opacity = "1";
    this.resultEl.style.opacity = "1";
  }

  hideLens() {
    this.lensEl.style.opacity = "0";
    this.resultEl.style.opacity = "0";
  }

  handleMouseMove(e) {
    if (this.isMobile || !this.rect) return;

    const x = e.clientX - this.rect.left;
    const y = e.clientY - this.rect.top;

    /* Lens dimensions = result panel size / zoom level */
    const lensW = this.resultEl.offsetWidth / this.zoomLevel;
    const lensH = this.resultEl.offsetHeight / this.zoomLevel;

    /* Clamp within image bounds */
    const lensX = Math.max(0, Math.min(x - lensW / 2, this.rect.width - lensW));
    const lensY = Math.max(0, Math.min(y - lensH / 2, this.rect.height - lensH));

    /* Position the lens indicator */
    this.lensEl.style.width = lensW + "px";
    this.lensEl.style.height = lensH + "px";
    this.lensEl.style.left = this.rect.left - this.mainEl.getBoundingClientRect().left + lensX + "px";
    this.lensEl.style.top = this.rect.top - this.mainEl.getBoundingClientRect().top + lensY + "px";

    /* Move the result background */
    const bgX = (lensX / (this.rect.width - lensW)) * 100;
    const bgY = (lensY / (this.rect.height - lensH)) * 100;
    this.resultEl.style.backgroundPosition = `${bgX}% ${bgY}%`;
  }

  /* ---- Mobile: Tap to Zoom + Pan ---- */

  initMobileZoom() {
    this.mobileZoomed = false;
    this.panStartX = 0;
    this.panStartY = 0;
    this.panOffsetX = 0;
    this.panOffsetY = 0;
    this.lastPanX = 0;
    this.lastPanY = 0;

    this.imgEl.addEventListener("click", (e) => {
      if (!this.isMobile) return;
      e.preventDefault();
      this.toggleMobileZoom(e);
    });

    this.imgEl.addEventListener("touchstart", (e) => {
      if (!this.mobileZoomed || e.touches.length !== 1) return;
      this.imgEl.classList.add("is-dragging");
      this.panStartX = e.touches[0].clientX - this.lastPanX;
      this.panStartY = e.touches[0].clientY - this.lastPanY;
    }, { passive: true });

    this.imgEl.addEventListener("touchmove", (e) => {
      if (!this.mobileZoomed || e.touches.length !== 1) return;
      e.preventDefault();
      this.lastPanX = e.touches[0].clientX - this.panStartX;
      this.lastPanY = e.touches[0].clientY - this.panStartY;
      this.applyMobileTransform();
    }, { passive: false });

    this.imgEl.addEventListener("touchend", () => {
      this.imgEl.classList.remove("is-dragging");
    });
  }

  toggleMobileZoom(e) {
    if (this.mobileZoomed) {
      this.resetMobileZoom();
    } else {
      this.mobileZoomed = true;
      this.imgEl.classList.add("is-zoomed");

      /* Zoom towards tap point */
      const rect = this.imgEl.getBoundingClientRect();
      const tapX = ((e.clientX || e.touches?.[0]?.clientX || rect.width / 2) - rect.left) / rect.width;
      const tapY = ((e.clientY || e.touches?.[0]?.clientY || rect.height / 2) - rect.top) / rect.height;

      const scale = 2.5;
      this.lastPanX = -(tapX * rect.width * (scale - 1));
      this.lastPanY = -(tapY * rect.height * (scale - 1));
      this.applyMobileTransform();
    }
  }

  resetMobileZoom() {
    this.mobileZoomed = false;
    this.lastPanX = 0;
    this.lastPanY = 0;
    this.imgEl.classList.remove("is-zoomed", "is-dragging");
    this.imgEl.style.transform = "";
  }

  applyMobileTransform() {
    this.imgEl.style.transform = `translate(${this.lastPanX}px, ${this.lastPanY}px) scale(2.5)`;
  }
}

/* Self-initialize */
document.addEventListener("DOMContentLoaded", () => {
  window.imageZoomModal = new ImageZoomModal();
});
